import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useAppDispatch } from '../../hooks/redux';
import { updatePortfolioItem } from '../../store/slices/userSlice';
import { Portfolio } from '../../types';
import Button from '../common/Button';
import TagSelector from '../common/TagSelector';
import useLockBodyScroll from '../../hooks/useLockBodyScroll';
import toast from 'react-hot-toast';
import LocationPicker from '../common/LocationPicker';
import useBackButton from '../../hooks/useBackButton';

interface EditPortfolioModalProps {
  portfolio: Portfolio;
  onClose: () => void;
  onSuccess?: () => void;
}

const EditPortfolioModal: React.FC<EditPortfolioModalProps> = ({ 
  portfolio, 
  onClose, 
  onSuccess 
}) => {
  useLockBodyScroll();
  useBackButton(onClose, true, 150);
  const [title, setTitle] = useState(portfolio.title);
  const [description, setDescription] = useState(portfolio.description || '');
  const [tags, setTags] = useState<string[]>(portfolio.tags || []);
  const [location, setLocation] = useState(portfolio.location || '');
  const [coordinates, setCoordinates] = useState<{lat: number, lng: number} | null>(
    (portfolio.latitude && portfolio.longitude) ? { lat: portfolio.latitude, lng: portfolio.longitude } : null
  );
  const [loading, setLoading] = useState(false);
  const dispatch = useAppDispatch();

  // Sync state with portfolio prop when it changes or loads
  useEffect(() => {
    if (portfolio) {
      setTitle(portfolio.title);
      setDescription(portfolio.description || '');
      setTags(portfolio.tags || []);
      setLocation(portfolio.location || '');
      setCoordinates(
        (portfolio.latitude && portfolio.longitude) ? { lat: portfolio.latitude, lng: portfolio.longitude } : null
      );
    }
  }, [portfolio]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      toast.error('Please provide a title');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('title', title.trim());
      formData.append('description', description.trim());
      formData.append('tags', tags.join(','));
      
      if (location) {
        formData.append('location', location);
        if (coordinates) {
          formData.append('latitude', coordinates.lat.toString());
          formData.append('longitude', coordinates.lng.toString());
        }
      } else {
        formData.append('location', '');
      }
      
      const result = await dispatch(updatePortfolioItem({ 
        id: portfolio.id, 
        data: formData 
      }));

      if (updatePortfolioItem.fulfilled.match(result)) {
        onSuccess?.();
      } else {
        toast.error(result.payload as string || 'Failed to update portfolio item');
      }
    } catch (error) {
      toast.error('Failed to update portfolio item');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[80] flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-white dark:bg-gray-900 rounded-2xl max-w-2xl w-full max-h-[90vh] flex flex-col shadow-2xl overflow-hidden border border-gray-100 dark:border-gray-800"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 md:p-6 border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 z-10 shrink-0">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Edit Portfolio Item</h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Update your work details</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
              disabled={loading}
            >
              <XMarkIcon className="h-6 w-6 text-gray-400 dark:text-gray-500" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-6">
            <form id="edit-portfolio-form" onSubmit={handleSubmit} className="space-y-6">
              {/* Portfolio Item Image (Read Only) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Portfolio Item Image
                </label>
                <div className="border border-gray-200 dark:border-gray-800 rounded-2xl p-4 text-center bg-gray-50/50 dark:bg-gray-800/20">
                  <img
                    src={portfolio.image_url_signed}
                    alt={portfolio.title}
                    className="mx-auto h-48 md:h-96 rounded-xl object-contain"
                  />
                </div>
              </div>

              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Title *
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-transparent text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                  placeholder="Give your work a compelling title..."
                  required
                  disabled={loading}
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Description
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={4}
                  className="w-full border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-transparent resize-none text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                  placeholder="Describe your work..."
                  disabled={loading}
                />
              </div>

              {/* Tags */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Tags
                </label>
                <TagSelector
                  selectedTags={tags}
                  onChange={setTags}
                  placeholder="Select tags to help others discover your work..."
                  maxTags={10}
                />
              </div>

              {/* Location */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Location (Optional)
                </label>
                <div className="relative z-50">
                  <LocationPicker
                    value={location}
                    onChange={(loc, coords) => {
                      setLocation(loc);
                      if (coords) setCoordinates(coords);
                    }}
                    initialCoordinates={coordinates || undefined}
                    placeholder="Where was this taken?"
                  />
                </div>
              </div>
            </form>
          </div>

          {/* Footer Actions */}
          <div className="p-4 md:p-6 border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900 shrink-0 flex flex-col space-y-3">
            <div className="flex space-x-3 w-full">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={loading}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                form="edit-portfolio-form"
                variant="primary"
                loading={loading}
                disabled={!title.trim() || loading}
                className="flex-1"
              >
                Update Portfolio
              </Button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default EditPortfolioModal;
